import { Component, type ErrorInfo, type ReactNode } from "react";
import { createLogger } from "../core/logger";

const log = createLogger("ui.errorBoundary");

type Props = {
  /** Shown in the fallback so the operator knows which panel died. */
  panelName: string;
  children: ReactNode;
};

type State = {
  error: Error | undefined;
};

/**
 * Isolates one panel's rendering failures from the rest of the app. A malformed SDK payload
 * feeding a JSON tree must never take down the whole diagnostic tool.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: undefined };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    log.error(`panel "${this.props.panelName}" threw during render`, {
      message: error.message,
      componentStack: info.componentStack,
    });
  }

  private readonly retry = (): void => {
    this.setState({ error: undefined });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (error) {
      return (
        <div className="error-boundary" role="alert">
          <p className="error-boundary__title">
            {this.props.panelName} panel crashed
          </p>
          <p className="error-boundary__message">{error.message}</p>
          <button type="button" onClick={this.retry}>
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
